<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div>
          <p class="text-uppercase text-muted mb-1 super-page-kicker">Integrations</p>
          <h2 class="h3 mb-0">API Tokens</h2>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">

      <!-- Header card -->
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <p class="super-page-kicker mb-2">Partner Access</p>
              <h3 class="mb-1">Integration Tokens</h3>
              <p class="text-muted mb-0">
                Issue bearer tokens to authorized external systems. Each token is scoped to a single site and validated against an IP whitelist.
                Tokens are never stored in plain text — save them immediately when created.
              </p>
            </div>
            <div class="col-lg-4 text-lg-right mt-3 mt-lg-0">
              <b-button variant="primary" @click="openCreateModal">
                <i class="fas fa-plus mr-2"></i> Create Token
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Token list -->
      <div class="card shadow-sm border-0">
        <div class="card-header bg-white border-0 py-3">
          <h3 class="mb-0">Active &amp; Revoked Tokens</h3>
        </div>

        <div v-if="loading" class="text-center py-6">
          <b-spinner variant="primary" class="mb-3"></b-spinner>
          <p class="text-muted mb-0">Loading tokens...</p>
        </div>

        <div v-else-if="tokenList.length === 0" class="text-center py-6">
          <i class="fas fa-key text-muted mb-3" style="font-size: 2rem;"></i>
          <h4 class="mb-1">No tokens yet</h4>
          <p class="text-muted mb-0">Create a token to grant a partner system access to this platform.</p>
        </div>

        <div v-else class="table-responsive">
          <b-table
            striped
            hover
            :items="tokenList"
            :fields="tableFields"
            class="mb-0"
          >
            <template slot="status" slot-scope="data">
              <b-badge :variant="data.item.status === 'active' ? 'success' : 'danger'" class="px-2 py-1">
                {{ data.item.status }}
              </b-badge>
            </template>

            <template slot="service_provider_id" slot-scope="data">
              {{ data.item.service_provider_id }}
            </template>

            <template slot="site_id" slot-scope="data">
              {{ data.item.site_id }}
            </template>

            <template slot="last_used_at" slot-scope="data">
              <span v-if="data.item.last_used_at">
                {{ data.item.last_used_at | fmtDateTime }}
              </span>
              <span v-else class="text-muted">Never</span>
            </template>

            <template slot="expires_at" slot-scope="data">
              <span v-if="data.item.expires_at">
                {{ data.item.expires_at | fmtDate }}
              </span>
              <span v-else class="text-muted">Never</span>
            </template>

            <template slot="ip_whitelist" slot-scope="data">
              <div class="d-flex flex-wrap" style="gap: 0.25rem; max-width: 220px;">
                <b-badge
                  v-for="ip in (data.item.ip_whitelist || [])"
                  :key="ip"
                  variant="light"
                  class="text-monospace border"
                  style="font-size: 0.75rem;"
                >{{ ip }}</b-badge>
                <span v-if="!data.item.ip_whitelist || !data.item.ip_whitelist.length" class="text-muted">—</span>
              </div>
            </template>

            <template slot="actions" slot-scope="data">
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="data.item.status === 'revoked'"
                @click="confirmRevoke(data.item)"
              >
                <i class="fas fa-ban mr-1"></i> Revoke
              </b-button>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <!-- ── Create Token Modal ── -->
    <b-modal
      ref="createModal"
      title="Create Integration Token"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      size="lg"
      centered
    >
      <b-form @submit.prevent="submitCreate">
        <div class="row">
          <b-form-group label="Token Name / Partner Label" class="col-md-12">
            <b-form-input
              v-model.trim="form.name"
              placeholder="e.g. Partner ABC"
              :state="formErrors.name ? false : null"
            ></b-form-input>
            <b-form-invalid-feedback>{{ formErrors.name }}</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <div class="row">
          <b-form-group label="Service Provider" class="col-md-6">
            <b-form-select
              v-model="form.service_provider_id"
              :options="providerOptions"
              :state="formErrors.service_provider_id ? false : null"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>Select a provider</b-form-select-option>
              </template>
            </b-form-select>
            <b-form-invalid-feedback>{{ formErrors.service_provider_id }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Site" class="col-md-6">
            <b-form-select
              v-model="form.site_id"
              :options="filteredSiteOptions"
              :disabled="!form.service_provider_id || sitesLoading"
              :state="formErrors.site_id ? false : null"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  <span v-if="!form.service_provider_id">Select a provider first</span>
                  <span v-else-if="sitesLoading">Loading sites…</span>
                  <span v-else-if="filteredSiteOptions.length === 0">No active sites for this provider</span>
                  <span v-else>Select a site</span>
                </b-form-select-option>
              </template>
            </b-form-select>
            <b-form-invalid-feedback>{{ formErrors.site_id }}</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <b-form-group label="Allowed IP Addresses / CIDR ranges">
          <div class="d-flex flex-wrap mb-2" style="gap: 0.35rem;" v-if="form.ip_addresses.length">
            <span
              v-for="(ip, idx) in form.ip_addresses"
              :key="idx"
              class="ip-chip"
            >
              <span class="ip-chip-text">{{ ip }}</span>
              <span class="ip-chip-remove" @click="removeIp(idx)">&times;</span>
            </span>
          </div>
          <div class="d-flex" style="gap: 0.5rem;">
            <b-form-input
              v-model.trim="ipInput"
              placeholder="e.g. 203.0.113.5 or 192.168.1.0/24"
              @keydown.enter.native.prevent="addIp"
              :state="ipInputError ? false : null"
            ></b-form-input>
            <b-button variant="outline-secondary" type="button" @click="addIp">Add</b-button>
          </div>
          <b-form-invalid-feedback :state="ipInputError ? false : null">{{ ipInputError }}</b-form-invalid-feedback>
          <small class="text-muted">Press Enter or click Add after each address. Use <code>0.0.0.0/0</code> to allow any IP (testing only).</small>
          <div v-if="formErrors.ip_addresses" class="text-danger small mt-1">{{ formErrors.ip_addresses }}</div>
        </b-form-group>

        <b-form-group label="Expiry Date (optional)">
          <b-form-input
            v-model="form.expires_at"
            type="date"
            :state="formErrors.expires_at ? false : null"
          ></b-form-input>
          <b-form-invalid-feedback>{{ formErrors.expires_at }}</b-form-invalid-feedback>
          <small class="text-muted">Leave blank for no expiry.</small>
        </b-form-group>

        <div class="d-flex justify-content-end mt-3" style="gap: 0.5rem;">
          <b-button variant="outline-secondary" type="button" @click="$refs.createModal.hide()">Cancel</b-button>
          <b-button variant="primary" type="submit" :disabled="creating">
            <b-spinner v-if="creating" small class="mr-1"></b-spinner>
            {{ creating ? 'Creating...' : 'Create Token' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- ── Token reveal Modal (shown once, no outside-click dismiss) ── -->
    <b-modal
      ref="tokenRevealModal"
      title="Token Created — Save It Now"
      ok-only
      ok-title="I have saved this token"
      no-close-on-backdrop
      no-close-on-esc
      header-bg-variant="warning"
      centered
      size="lg"
    >
      <div class="alert alert-warning d-flex align-items-start mb-4">
        <i class="fas fa-exclamation-triangle mr-3 mt-1" style="font-size: 1.2rem; flex-shrink: 0;"></i>
        <div>
          <strong>Copy this token now.</strong> It will <strong>not</strong> be shown again — it is never stored in plain text.
          If you lose it, a new token must be created.
        </div>
      </div>

      <div class="mb-3">
        <label class="font-weight-bold text-sm text-uppercase text-muted mb-1">Token Name</label>
        <p class="mb-0 font-weight-bold">{{ createdToken.name }}</p>
      </div>

      <div class="mb-3">
        <label class="font-weight-bold text-sm text-uppercase text-muted mb-1">Bearer Token</label>
        <div class="d-flex align-items-center" style="gap: 0.5rem;">
          <code
            class="d-block p-3 rounded border bg-light text-break flex-grow-1"
            style="font-size: 0.82rem; word-break: break-all; user-select: all;"
          >{{ createdToken.token }}</code>
          <b-button variant="outline-primary" size="sm" @click="copyToken" style="flex-shrink: 0;">
            <i :class="copied ? 'fas fa-check' : 'fas fa-copy'"></i>
            {{ copied ? 'Copied!' : 'Copy' }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ── Revoke Confirm Modal ── -->
    <b-modal
      ref="revokeModal"
      title="Revoke Token"
      no-close-on-backdrop
      no-close-on-esc
      centered
      hide-footer
    >
      <div class="alert alert-danger">
        <i class="fas fa-ban mr-2"></i>
        <strong>Revoking this token will immediately cut off the partner's access. This cannot be undone.</strong>
      </div>
      <p class="mb-1">Token: <strong>{{ revokeTarget && revokeTarget.name }}</strong></p>
      <p class="text-muted small mb-4">
        Revoked tokens cannot be re-activated. A new token must be created if access needs to be restored.
      </p>
      <div class="d-flex justify-content-end" style="gap: 0.5rem;">
        <b-button variant="outline-secondary" @click="$refs.revokeModal.hide()">Cancel</b-button>
        <b-button variant="danger" :disabled="revoking" @click="doRevoke">
          <b-spinner v-if="revoking" small class="mr-1"></b-spinner>
          {{ revoking ? 'Revoking...' : 'Yes, Revoke Token' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ApiServices from '@/services/api'
import userData from '@/pages/Common/userData'

export default {
  components: { userData },
  data() {
    return {
      loading: true,
      creating: false,
      revoking: false,
      copied: false,
      revokeTarget: null,
      ipInput: '',
      ipInputError: '',
      providerSites: [],
      sitesLoading: false,
      createdToken: { name: '', token: '' },
      form: {
        name: '',
        service_provider_id: null,
        site_id: null,
        ip_addresses: [],
        expires_at: ''
      },
      formErrors: {},
      tableFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'service_provider_id', label: 'Provider', sortable: true, class: 'text-center' },
        { key: 'site_id', label: 'Site', sortable: true, class: 'text-center' },
        { key: 'status', label: 'Status', sortable: true, class: 'text-center' },
        { key: 'last_used_at', label: 'Last Used', sortable: true },
        { key: 'expires_at', label: 'Expires', sortable: true },
        { key: 'ip_whitelist', label: 'Allowed IPs' },
        { key: 'actions', label: '', class: 'text-right' }
      ]
    }
  },
  computed: {
    ...mapGetters('integrationTokens', ['tokenList']),
    ...mapGetters('serviceProvider', ['serviceProviderList']),
    providerOptions() {
      const providers = (this.serviceProviderList && this.serviceProviderList.data) || []
      return providers.map(p => ({ value: p.id, text: `${p.name} (ID: ${p.id})` }))
    },
    filteredSiteOptions() {
      return this.providerSites.map(s => ({ value: s.id, text: s.name }))
    }
  },
  watch: {
    'form.service_provider_id'(newVal) {
      this.form.site_id = null
      this.providerSites = []
      if (!newVal) return
      this.sitesLoading = true
      ApiServices.fetchSitesByProvider(newVal)
        .then(response => {
          this.providerSites = (response && response.data) || []
        })
        .finally(() => {
          this.sitesLoading = false
        })
    }
  },
  methods: {
    ...mapActions('integrationTokens', ['FETCH_TOKENS', 'CREATE_TOKEN', 'REVOKE_TOKEN']),
    ...mapActions('serviceProvider', ['SERVICE_PROVIDER_LIST']),

    openCreateModal() {
      this.form = { name: '', service_provider_id: null, site_id: null, ip_addresses: [], expires_at: '' }
      this.formErrors = {}
      this.ipInput = ''
      this.ipInputError = ''
      this.$refs.createModal.show()
    },

    addIp() {
      const ip = this.ipInput.trim()
      if (!ip) return
      if (this.form.ip_addresses.includes(ip)) {
        this.ipInputError = 'Already added'
        return
      }
      this.form.ip_addresses.push(ip)
      this.ipInput = ''
      this.ipInputError = ''
    },

    removeIp(idx) {
      this.form.ip_addresses.splice(idx, 1)
    },

    submitCreate() {
      this.formErrors = {}
      let valid = true

      if (!this.form.name) {
        this.formErrors.name = 'Token name is required'
        valid = false
      }
      if (!this.form.service_provider_id) {
        this.formErrors.service_provider_id = 'Service provider ID is required'
        valid = false
      }
      if (!this.form.site_id) {
        this.formErrors.site_id = 'Site ID is required'
        valid = false
      }
      if (!this.form.ip_addresses.length) {
        this.formErrors.ip_addresses = 'At least one IP address or CIDR range is required'
        valid = false
      }
      if (!valid) return

      const payload = {
        name: this.form.name,
        service_provider_id: this.form.service_provider_id,
        site_id: this.form.site_id,
        ip_addresses: this.form.ip_addresses
      }
      if (this.form.expires_at) payload.expires_at = this.form.expires_at

      this.creating = true
      this.CREATE_TOKEN(payload)
        .then(response => {
          this.$refs.createModal.hide()
          this.createdToken = {
            name: response.data.name,
            token: response.data.token
          }
          this.copied = false
          this.$refs.tokenRevealModal.show()
          this.FETCH_TOKENS()
        })
        .catch(err => {
          if (err && err.errors) {
            Object.keys(err.errors).forEach(k => {
              this.$set(this.formErrors, k, err.errors[k][0])
            })
          }
        })
        .finally(() => {
          this.creating = false
        })
    },

    copyToken() {
      if (!navigator.clipboard) return
      navigator.clipboard.writeText(this.createdToken.token).then(() => {
        this.copied = true
        setTimeout(() => { this.copied = false }, 2500)
      })
    },

    confirmRevoke(token) {
      this.revokeTarget = token
      this.$refs.revokeModal.show()
    },

    doRevoke() {
      this.revoking = true
      this.REVOKE_TOKEN(this.revokeTarget.id)
        .then(() => {
          this.$refs.revokeModal.hide()
          this.revokeTarget = null
        })
        .catch(() => {})
        .finally(() => {
          this.revoking = false
        })
    }
  },
  created() {
    Promise.all([
      this.FETCH_TOKENS(),
      this.SERVICE_PROVIDER_LIST(1)
    ]).finally(() => {
      this.loading = false
    })
  }
}
</script>

<style scoped>
.super-page-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #5e72e4;
}

.ip-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.55rem;
  background: #e8ecff;
  border: 1px solid #b8c4f8;
  border-radius: 0.4rem;
  font-size: 0.8rem;
  font-family: monospace;
  font-weight: 600;
  color: #3d55df;
}

.ip-chip-remove {
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  color: #7b8fd4;
  transition: color 0.15s ease;
}

.ip-chip-remove:hover {
  color: #e63946;
}

.py-6 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}
</style>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/integrationTokens.vue